import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useSwipe } from "../hooks/useSwipe";

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

function AuthPhoto({ filename, alt, className }) {
  const [src, setSrc] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function loadPhoto() {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/photos/${filename}`, { headers });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (!cancelled) { objectUrlRef.current = url; setSrc(url); }
      } catch (err) { console.error("Photo load error:", err); }
    }
    if (filename) loadPhoto();
    return () => { cancelled = true; if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); };
  }, [filename]);

  if (!src) return <div className="photo-placeholder" />;
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

export default function Albums({ role, canDelete }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingAlbum, setDeletingAlbum] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [checkedPhotos, setCheckedPhotos] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [settingCover, setSettingCover] = useState(null);
  const [message, setMessage] = useState(null);

  // Rename album modal
  const [showRename, setShowRename] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");
  const [renameDate, setRenameDate] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Move photos modal
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moving, setMoving] = useState(false);
  const [movePhoto, setMovePhoto] = useState(null); // single photo move

  useEffect(() => { fetchAlbums(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyDown = useCallback((e) => {
    if (selected === null) return;
    if (e.key === "ArrowRight") goNext();
    if (e.key === "ArrowLeft") goPrev();
    if (e.key === "Escape") closeLightbox();
  }, [selected, selectedIndex, albumPhotos]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  function showMessage(type, text) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  }

  async function fetchAlbums() {
    const { data, error } = await supabase
      .from("albums")
      .select("*, photos(count)")
      .order("date", { ascending: false });
    if (!error) setAlbums(data);
    setLoading(false);
  }

  async function openAlbum(album) {
    setSelectedAlbum(album);
    setSelectMode(false);
    setCheckedPhotos([]);
    setMessage(null);
    const { data } = await supabase
      .from("photos")
      .select("*")
      .eq("album_id", album.id)
      .order("uploaded_at", { ascending: true });
    setAlbumPhotos(data || []);
  }

  // ─── Rename Album ───────────────────────────────────────────────────────
  function openRename() {
    setRenameName(selectedAlbum.name);
    setRenameDesc(selectedAlbum.description || "");
    setRenameDate(selectedAlbum.date || "");
    setShowRename(true);
  }

  async function handleRename(e) {
    e.preventDefault();
    setRenaming(true);
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/albums/${selectedAlbum.id}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name: renameName, description: renameDesc, date: renameDate }),
    });
    if (res.ok) {
      const updated = { ...selectedAlbum, name: renameName, description: renameDesc, date: renameDate };
      setSelectedAlbum(updated);
      setAlbums((prev) => prev.map((a) => a.id === selectedAlbum.id ? { ...a, ...updated } : a));
      showMessage("success", "Album updated!");
      setShowRename(false);
    } else {
      showMessage("error", "Failed to update album.");
    }
    setRenaming(false);
  }

  // ─── Delete Album ───────────────────────────────────────────────────────
  async function deleteAlbum() {
    if (!window.confirm(
      `Delete album "${selectedAlbum.name}"?\n\nThis will permanently delete the album and all ${albumPhotos.length} photo(s) inside it.\n\nThis cannot be undone.`
    )) return;

    setDeletingAlbum(true);
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/albums/${selectedAlbum.id}`, {
      method: "DELETE",
      headers,
    });

    if (res.ok) {
      setAlbums((prev) => prev.filter((a) => a.id !== selectedAlbum.id));
      setSelectedAlbum(null);
      setAlbumPhotos([]);
    } else {
      showMessage("error", "Failed to delete album.");
    }
    setDeletingAlbum(false);
  }

  // ─── Move Photos ────────────────────────────────────────────────────────
  function openMoveModal(photo = null) {
    setMovePhoto(photo);
    setMoveTarget("");
    setShowMoveModal(true);
  }

  async function handleMove(e) {
    e.preventDefault();
    if (!moveTarget) return;
    setMoving(true);
    const headers = await getAuthHeaders();

    const photosToMove = movePhoto ? [movePhoto] : checkedPhotos;

    for (const photo of photosToMove) {
      await fetch(`/api/photos/${photo.id}/move`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ album_id: moveTarget }),
      });
    }

    const movedIds = photosToMove.map((p) => p.id);
    setAlbumPhotos((prev) => prev.filter((p) => !movedIds.includes(p.id)));
    setCheckedPhotos([]);
    setSelectMode(false);
    setShowMoveModal(false);
    setMovePhoto(null);
    showMessage("success", `${photosToMove.length} photo(s) moved successfully!`);
    setMoving(false);
  }

  // ─── Cover Photo ────────────────────────────────────────────────────────
  async function setCoverPhoto(photo) {
    setSettingCover(photo.id);
    const { error } = await supabase
      .from("albums")
      .update({ cover_photo: photo.file_path })
      .eq("id", selectedAlbum.id);

    if (!error) {
      setSelectedAlbum((prev) => ({ ...prev, cover_photo: photo.file_path }));
      setAlbums((prev) => prev.map((a) =>
        a.id === selectedAlbum.id ? { ...a, cover_photo: photo.file_path } : a
      ));
      showMessage("success", "Cover photo updated!");
    }
    setSettingCover(null);
  }

  function openPhoto(photo, index) {
    if (selectMode) { toggleCheck(photo); return; }
    setSelected(photo);
    setSelectedIndex(index);
  }

  function closeLightbox() { setSelected(null); setSelectedIndex(null); }

  function goNext() {
    const nextIndex = (selectedIndex + 1) % albumPhotos.length;
    setSelected(albumPhotos[nextIndex]);
    setSelectedIndex(nextIndex);
  }

  function goPrev() {
    const prevIndex = (selectedIndex - 1 + albumPhotos.length) % albumPhotos.length;
    setSelected(albumPhotos[prevIndex]);
    setSelectedIndex(prevIndex);
  }

  const swipeHandlers = useSwipe({ onSwipeLeft: goNext, onSwipeRight: goPrev });

  function toggleCheck(photo) {
    setCheckedPhotos((prev) =>
      prev.find((p) => p.id === photo.id)
        ? prev.filter((p) => p.id !== photo.id)
        : [...prev, photo]
    );
  }

  function selectAll() { setCheckedPhotos([...albumPhotos]); }
  function clearSelection() { setCheckedPhotos([]); setSelectMode(false); }

  async function deleteSinglePhoto(photo) {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    setDeleting(true);
    const headers = await getAuthHeaders();
    const { error } = await supabase.from("photos").delete().eq("id", photo.id);
    if (!error) {
      await fetch(`/api/delete`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename: photo.file_path }),
      });
      const updated = albumPhotos.filter((p) => p.id !== photo.id);
      setAlbumPhotos(updated);
      if (updated.length === 0) closeLightbox();
      else {
        const nextIndex = Math.min(selectedIndex, updated.length - 1);
        setSelected(updated[nextIndex]);
        setSelectedIndex(nextIndex);
      }
    }
    setDeleting(false);
  }

  async function bulkDelete() {
    if (checkedPhotos.length === 0) return;
    if (!window.confirm(`Delete ${checkedPhotos.length} photo(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const headers = await getAuthHeaders();
    for (const photo of checkedPhotos) {
      await supabase.from("photos").delete().eq("id", photo.id);
      await fetch(`/api/delete`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename: photo.file_path }),
      });
    }
    const deletedIds = checkedPhotos.map((p) => p.id);
    setAlbumPhotos((prev) => prev.filter((p) => !deletedIds.includes(p.id)));
    setCheckedPhotos([]);
    setSelectMode(false);
    setBulkDeleting(false);
  }

  // Other albums for move dropdown
  const otherAlbums = albums.filter((a) => a.id !== selectedAlbum?.id);

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  // ─── Album List View ────────────────────────────────────────────────────
  if (!selectedAlbum) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Albums</h1>
          <p className="page-subtitle">{albums.length} albums</p>
        </div>
        {albums.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <p>No albums yet — check back soon!</p>
          </div>
        ) : (
          <div className="album-grid">
            {albums.map((album) => (
              <div key={album.id} className="album-card-wrapper">
                <div className="album-card" onClick={() => openAlbum(album)}>
                  <div className="album-cover">
                    {album.cover_photo
                      ? <AuthPhoto filename={album.cover_photo} alt={album.name} className="album-cover-img" />
                      : <div className="album-cover-placeholder">📁</div>}
                  </div>
                  <div className="album-info">
                    <div className="album-name">{album.name}</div>
                    <div className="album-date">
                      {new Date(album.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </div>
                    {album.description && <div className="album-desc">{album.description}</div>}
                    {album.cover_photo && <div className="album-has-cover">📸 Cover set</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Album Photo View ───────────────────────────────────────────────────
  return (
    <div className="page">
      {/* Rename Modal */}
      {showRename && (
        <div className="modal-overlay" onClick={() => setShowRename(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Edit Album</h2>
            <form onSubmit={handleRename} className="modal-form">
              <div className="form-group">
                <label className="form-label">Album Name</label>
                <input className="form-input" type="text" value={renameName} onChange={(e) => setRenameName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input className="form-input" type="date" value={renameDate} onChange={(e) => setRenameDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <input className="form-input" type="text" value={renameDesc} onChange={(e) => setRenameDesc(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button type="button" className="modal-cancel" onClick={() => setShowRename(false)}>Cancel</button>
                <button type="submit" className="modal-confirm" disabled={renaming}>
                  {renaming ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Move Photos Modal */}
      {showMoveModal && (
        <div className="modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              Move {movePhoto ? "Photo" : `${checkedPhotos.length} Photo(s)`}
            </h2>
            <p className="modal-subtitle">Select the album to move to:</p>
            <form onSubmit={handleMove} className="modal-form">
              <div className="form-group">
                <label className="form-label">Destination Album</label>
                <select className="form-input" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} required>
                  <option value="">Choose album...</option>
                  {otherAlbums.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="modal-cancel" onClick={() => setShowMoveModal(false)}>Cancel</button>
                <button type="submit" className="modal-confirm" disabled={moving || !moveTarget}>
                  {moving ? "Moving..." : "Move Photos"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <button className="back-btn" onClick={() => { setSelectedAlbum(null); setAlbumPhotos([]); clearSelection(); }}>
          ← Back to Albums
        </button>
        <div className="album-page-header">
          <div>
            <h1 className="page-title">{selectedAlbum.name}</h1>
            <p className="page-subtitle">
              {new Date(selectedAlbum.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              {selectedAlbum.description && ` · ${selectedAlbum.description}`}
            </p>
          </div>

          <div className="admin-controls">
            {/* Album management buttons */}
            {role === "admin" && (
              <>
                <button className="select-btn" onClick={openRename}>✏️ Edit Album</button>
                <button className="delete-album-full-btn" onClick={deleteAlbum} disabled={deletingAlbum}>
                  {deletingAlbum ? "Deleting..." : "🗑️ Delete Album"}
                </button>
              </>
            )}

            {/* Photo selection */}
            {role === "admin" && albumPhotos.length > 0 && (
              !selectMode ? (
                <button className="select-btn" onClick={() => setSelectMode(true)}>☑️ Select</button>
              ) : (
                <div className="select-actions">
                  <span className="select-count">{checkedPhotos.length} selected</span>
                  <button className="select-btn" onClick={selectAll}>All</button>
                  <button className="select-btn" onClick={clearSelection}>Cancel</button>
                  {checkedPhotos.length > 0 && otherAlbums.length > 0 && (
                    <button className="move-btn" onClick={() => openMoveModal()}>
                      📦 Move ({checkedPhotos.length})
                    </button>
                  )}
                  <button className="delete-bulk-btn" onClick={bulkDelete} disabled={checkedPhotos.length === 0 || bulkDeleting}>
                    {bulkDeleting ? "Deleting..." : `🗑️ Delete (${checkedPhotos.length})`}
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {message && (
        <div className={message.type === "success" ? "message-success" : "error-banner"}>
          {message.type === "success" ? "✅" : "⚠️"} {message.text}
        </div>
      )}

      {role === "admin" && albumPhotos.length > 0 && !selectMode && (
        <div className="cover-hint">
          💡 Hover over a photo to <strong>Set as Cover</strong> or <strong>Move</strong> it to another album
        </div>
      )}

      {albumPhotos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📷</div>
          <p>No photos in this album yet!</p>
        </div>
      ) : (
        <div className="photo-grid">
          {albumPhotos.map((photo, index) => {
            const isChecked = checkedPhotos.find((p) => p.id === photo.id);
            const isCover = selectedAlbum.cover_photo === photo.file_path;
            return (
              <div
                key={photo.id}
                className={`photo-card ${selectMode ? "selectable" : ""} ${isChecked ? "photo-checked" : ""} ${isCover ? "photo-is-cover" : ""}`}
                onClick={() => openPhoto(photo, index)}
              >
                <AuthPhoto filename={photo.file_path} alt={photo.caption || "Family photo"} className="photo-img" />
                {isCover && <div className="cover-badge">⭐ Cover</div>}

                {role === "admin" && !selectMode && (
                  <div className="photo-admin-overlay">
                    {!isCover && (
                      <button className="photo-overlay-btn" onClick={(e) => { e.stopPropagation(); setCoverPhoto(photo); }} disabled={settingCover === photo.id}>
                        {settingCover === photo.id ? "..." : "⭐ Cover"}
                      </button>
                    )}
                    {otherAlbums.length > 0 && (
                      <button className="photo-overlay-btn" onClick={(e) => { e.stopPropagation(); openMoveModal(photo); }}>
                        📦 Move
                      </button>
                    )}
                  </div>
                )}

                {selectMode && (
                  <div className={`check-overlay ${isChecked ? "checked" : ""}`}>{isChecked ? "✓" : ""}</div>
                )}
                {photo.caption && <div className="photo-caption">{photo.caption}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {selected && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()} {...swipeHandlers}>
            <button className="lightbox-close" onClick={closeLightbox}>✕</button>
            <div className="lightbox-counter">{selectedIndex + 1} / {albumPhotos.length}</div>
            <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} disabled={albumPhotos.length <= 1}>‹</button>
            <AuthPhoto filename={selected.file_path} alt={selected.caption || "Family photo"} className="lightbox-img" />
            <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); goNext(); }} disabled={albumPhotos.length <= 1}>›</button>
            <div className="lightbox-footer">
              {selected.caption && <p className="lightbox-caption">{selected.caption}</p>}
              {role === "admin" && selectedAlbum.cover_photo !== selected.file_path && (
                <button className="set-cover-btn-lightbox" onClick={() => { setCoverPhoto(selected); closeLightbox(); }}>
                  ⭐ Set as Cover
                </button>
              )}
              {role === "admin" && otherAlbums.length > 0 && (
                <button className="set-cover-btn-lightbox" onClick={() => { closeLightbox(); openMoveModal(selected); }}>
                  📦 Move to Album
                </button>
              )}
              {canDelete && (
                <button className="delete-btn" onClick={() => deleteSinglePhoto(selected)} disabled={deleting}>
                  {deleting ? "Deleting..." : "🗑️ Delete Photo"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}